import { FC, memo, SVGProps, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import {
  BankTransfer,
  CardIcon,
  Scan,
  USSD,
} from "@zenith/assets/icons/components";
import {
  ButtonComponent,
  MajorButtonComponent,
  ToastSnackbarComponent,
} from "@zenith/components";
import { PaymentMethods } from "@zenith/components/ui/BarGraphicComponent/bar-graphic-component/enum";
import { usePaymentLinkContext } from "@zenith/context/global-context/hooks";
import {
  EXPIRATION_DATE_FOR_PAYMENTS,
  EXPIRATION_DATE_FOR_PENDING_CARD_ITEM,
  MILLISECONDS,
  SUCCESS_API_CODE,
} from "@zenith/data/constants";
import {
  PAYMENT_LINK,
  PAYMENT_LINK_BANK_TRF_DETAILS,
  PAYMENT_LINK_CARD_DETAILS,
  PAYMENT_LINK_CARD_PENDING,
  PAYMENT_LINK_OVERVIEW_PAGE,
  PAYMENT_LINK_QR_CODE_DETAILS,
  PAYMENT_LINK_USSD_CODE_DETAILS,
} from "@zenith/routes/paths";
import {
  ApiResponseDTOListProviderRecord,
  ApiResponseDTOOperationDTO,
  CancelablePromise,
  OperationDTO,
  PaymentLinkBuyerRecord,
  PaymentLinkDTO,
  PaymentLinkPublicApiService,
} from "@zenith/services/transactions";
import { useNotificationContext } from "@zenith/context";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";

/**
 * Props for Payment Link Method Component.
 * @interface
 * @name PaymentLinkMethodComponentProps
 * @kind interface
 * @exports
 */
/* eslint-disable-next-line */
export interface PaymentLinkMethodComponentProps {}

/**
 * Payment Link Method Interface.
 * @interface
 * @name PaymentLinkMethod
 * @kind interface
 * @exports
 */
export interface PaymentLinkMethod {
  /**
   * Text of the Payment Link Method.
   * @property
   * @name text
   * @kind property
   * @memberof PaymentLinkMethod
   * @type {PaymentMethods}
   */
  text: PaymentMethods;
  /**
   * Icon of the Payment Link Method.
   * @property
   * @name icon
   * @kind property
   * @memberof PaymentLinkMethod
   * @type {FC<SVGProps<SVGSVGElement>>}
   */
  icon: FC<SVGProps<SVGSVGElement>>;
  /**
   * ID of the Payment Link Method.
   * @property
   * @name id
   * @kind property
   * @memberof PaymentLinkMethod
   * @type {number}
   */
  id: number;
  /**
   * Route of the Payment Link Method.
   * @property
   * @name route
   * @kind property
   * @memberof PaymentLinkMethod
   * @type {string}
   */
  route: string;
}

/**
 * Memoized Payment Link Method Component.
 * @constant
 * @name PaymentLinkMethodComponent
 * @kind variable
 * @type {React.NamedExoticComponent<PaymentLinkMethodComponentProps>}
 */
const PaymentLinkMethodComponent: React.NamedExoticComponent<PaymentLinkMethodComponentProps> =
  memo(
    /**
     * Payment Link Method Component.
     * @function
     * @name PaymentLinkMethodComponent
     * @kind function
     * @memberof PaymentLinkMethodComponent
     * @param {PaymentLinkMethodComponentProps} props Props for Payment Link Method Component.
     * @returns {JSX.Element} Payment Link Method Component.
     */
    function PaymentLinkMethodComponent(
      props: PaymentLinkMethodComponentProps
    ): JSX.Element {
      const buttons: PaymentLinkMethod[] = [
        {
          text: PaymentMethods.IN_BANK_TRANSFER,
          icon: BankTransfer,
          id: 0,
          route: PAYMENT_LINK_BANK_TRF_DETAILS,
        },
        {
          text: PaymentMethods.CARD,
          icon: CardIcon,
          id: 1,
          route: PAYMENT_LINK_CARD_DETAILS,
        },
        {
          text: PaymentMethods.QR_CODE,
          icon: Scan,
          id: 2,
          route: PAYMENT_LINK_QR_CODE_DETAILS,
        },
        {
          text: PaymentMethods.USSD,
          icon: USSD,
          id: 3,
          route: PAYMENT_LINK_USSD_CODE_DETAILS,
        },
      ];

      const navigate = useNavigate();
      const location = useLocation();
      const [methodSelected, setMethodSelectedState] = useState<
        PaymentLinkMethod | undefined
      >(undefined);
      const { paymentLinkContext, setPaymentLinkContext } =
        usePaymentLinkContext();
      const [paymentLinkMethod, setPaymentLinkMethod] = useState<
        | CancelablePromise<ApiResponseDTOOperationDTO>
        | CancelablePromise<ApiResponseDTOListProviderRecord>
      >();
      const [isLoading, setIsLoading] = useState<boolean>(false);

      const { setOpen } = useNotificationContext();

      const [toasterData, setToasterData] = useState<{
        title: string;
        message: string;
        status: "success" | "error" | "warning";
      }>({
        title: "",
        message: "",
        status: "success",
      });

      function notify(
        title: string,
        message: string,
        status: "success" | "error" | "warning"
      ): void {
        setToasterData({
          title,
          message,
          status,
        });
        setOpen(true);
      }

      const operationId =
        location.state?.operationId ||
        paymentLinkContext.operationDetails?.operationId;

      function handleBackButton() {
        navigate(`${PAYMENT_LINK}${operationId}`, {
          state: { operationId: operationId },
        });
      }

      function handleSubmit() {
        if (methodSelected && methodSelected.route) {
          submitPaymentMethod();
        }
      }

      function navigateToDetails() {
        if (methodSelected && methodSelected.route) {
          [
            PaymentMethods.IN_BANK_TRANSFER,
            PaymentMethods.QR_CODE,
            PaymentMethods.USSD,
          ].includes(methodSelected.text) &&
            navigate(methodSelected.route, {
              state: { operationId: operationId },
            });
        }
      }

      function submitPaymentMethod() {
        if (operationId) {
          const requestBody: PaymentLinkBuyerRecord = {
            countryDialingCode: paymentLinkContext.clientData.countryCode,
            email: paymentLinkContext.clientData.email,
            name: paymentLinkContext.clientData.fullName,
            phoneNumber: paymentLinkContext.clientData.phoneNumber,
          };

          setIsLoading(true);
          switch (methodSelected?.route) {
            case PAYMENT_LINK_CARD_DETAILS:
              setPaymentLinkMethod(
                PaymentLinkPublicApiService.postPaymentLinkCard(operationId, {
                  buyer: requestBody,
                })
              );
              break;
            case PAYMENT_LINK_BANK_TRF_DETAILS:
              setPaymentLinkMethod(
                PaymentLinkPublicApiService.postPaymentLinkInBankTransfer(
                  operationId,
                  { buyer: requestBody }
                )
              );
              break;
            case PAYMENT_LINK_USSD_CODE_DETAILS:
              navigate(PAYMENT_LINK_USSD_CODE_DETAILS, {
                state: { operationId: operationId },
              });
              break;
            case PAYMENT_LINK_QR_CODE_DETAILS:
              setPaymentLinkMethod(
                PaymentLinkPublicApiService.postPaymentLinkQrCode(operationId, {
                  buyer: requestBody,
                })
              );
              break;
            default:
              throw new Error("Payment method not mapped.");
          }
        }
      }

      function handleCardPayment(
        paymentLink?: string,
        expirationDate?: string
      ) {
        if (!paymentLink || !expirationDate) return;

        const initialRemainingSeconds = Math.floor(
          (new Date(expirationDate as string).getTime() -
            new Date().getTime()) /
            MILLISECONDS
        );

        if (initialRemainingSeconds <= 0) return;

        const expirationDateForCard = Date.now() + EXPIRATION_DATE_FOR_PAYMENTS;

        localStorage.setItem(
          EXPIRATION_DATE_FOR_PENDING_CARD_ITEM,
          expirationDateForCard.toString()
        );

        navigate(PAYMENT_LINK_CARD_PENDING, {
          state: { operationId: operationId },
        });

        const newWindow = window.open(paymentLink, "_blank");
        newWindow?.focus();
      }

      useEffect(() => {
        if (paymentLinkMethod) {
          paymentLinkMethod
            .then((response: ApiResponseDTOOperationDTO) => {
              setIsLoading(false);
              if (
                response &&
                "data" in response &&
                response.internalCode === SUCCESS_API_CODE
              ) {
                setPaymentLinkContext({
                  ...paymentLinkContext,
                  operationDetails: response.data as OperationDTO,
                  bankInfo: {
                    bankCode: "",
                    bankName: "",
                    ussdCode: "",
                  },
                });

                const paymentMethod =
                  response.data?.paymentLink?.paymentLinkMethod;

                if (paymentMethod === PaymentLinkDTO.paymentLinkMethod.CARD) {
                  const paymentLink = response.data?.paymentLink?.operationCode;
                  const expirationDate =
                    response.data?.paymentLink?.expirationDate;
                  handleCardPayment(paymentLink, expirationDate);
                } else {
                  navigateToDetails();
                }
              }
            })
            .catch((error) => {
              setIsLoading(false);
              const errorMessage =
                error?.body?.message || "Failed to process payment method";
              notify("Error", errorMessage, "error");
              console.error(error);
            });
        }
      }, [paymentLinkMethod]);

      return (
        <div className="w-full items-start justify-between px-2">
          <ToastSnackbarComponent
            horizontal="center"
            status={toasterData.status}
            vertical="top"
            hasAutoHideDuration
            size="lg"
            title={toasterData.title}
            text={toasterData.message}
          />
          <ButtonComponent
            label="Back"
            onClick={handleBackButton}
            id="back-to-dashboard"
            size="large"
            type="tertiary"
            iconStart={<ChevronLeft />}
          />
          <div className="m-auto mt-5 w-full rounded-1.25l border border-neutral-200">
            <div className=" rounded-2.5xl bg-white py-6 px-8">
              <div className="flex-col justify-between">
                <h1 className="mb-8 text-2xl font-medium text-neutral-900">
                  Payment Options
                </h1>
                <h2 className="text-sm font-medium text-neutral-500">
                  Select payment options
                </h2>
              </div>

              <div className="my-16 h-fit overflow-y-auto">
                <div className="flex flex-wrap justify-center gap-6">
                  {buttons.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex flex-col items-center justify-center"
                    >
                      <button
                        className={`${
                          methodSelected && methodSelected.id === payment.id
                            ? "bg-primary-300"
                            : "bg-neutral-200"
                        } flex h-24 w-24 items-center justify-center rounded-1.5lg`}
                        onClick={() => setMethodSelectedState(payment)}
                        disabled={isLoading}
                      >
                        <payment.icon
                          className={`transition-colors duration-300 ease-in-out group-hover:fill-white ${
                            methodSelected && methodSelected.id === payment.id
                              ? "fill-white"
                              : ""
                          }`}
                          style={{ transform: "scale(1)" }}
                        />
                      </button>
                      <p
                        className={`flex text-base transition-colors duration-300 ease-in-out group-hover:text-white ${
                          methodSelected && methodSelected.id === payment.id
                            ? "text-primary-300"
                            : ""
                        }`}
                      >
                        {payment.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-30 flex justify-center">
                <MajorButtonComponent
                  id="confirm-payment-link-button"
                  type="button"
                  buttonType="primary"
                  size="large"
                  onClick={handleSubmit}
                  isLoading={isLoading}
                  disabled={
                    !methodSelected ||
                    methodSelected.id === undefined ||
                    isLoading
                  }
                >
                  Confirm
                </MajorButtonComponent>
              </div>
            </div>
          </div>
        </div>
      );
    }
  );

export default PaymentLinkMethodComponent;
